import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  UserGroupIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
  PlusIcon,
  AcademicCapIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import { H1, H2, Body, Card, Button } from '../ui';
import StatisticsCard from '../ui/StatisticsCard';
import { useAuth } from '../../contexts/AuthContext';
import { TeacherDashboardStats } from '../../services/api/dashboard';
import { ClassroomWithStats } from '../../services/api/classrooms';
import { formatRelative } from '../../utils/date';

interface TeacherDashboardProps {
  stats?: TeacherDashboardStats;
  classrooms?: ClassroomWithStats[];
}

const TeacherDashboard: React.FC<TeacherDashboardProps> = ({ stats, classrooms }) => {
  const { user } = useAuth();

  const quickActions = [
    {
      title: 'Add Observation',
      description: 'Record a new child observation',
      icon: PlusIcon,
      href: '/observations?action=create',
      color: 'primary' as const,
    },
    {
      title: 'My Class',
      description: 'View and manage your class',
      icon: UserGroupIcon,
      href: '/children',
      color: 'secondary' as const,
    },
    {
      title: 'Assessment Reports',
      description: 'Create and view assessment reports',
      icon: ChartBarIcon,
      href: '/assessments',
      color: 'success' as const,
    },
    {
      title: 'My Observations',
      description: 'Review your observation history',
      icon: ClipboardDocumentListIcon,
      href: '/observations',
      color: 'warning' as const,
    },
  ];

  return (
    <div className="space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <H1 className="mb-2">Good morning, {user?.first_name}!</H1>
        <Body className="text-gray-600">
          Ready to make a difference in your children's lives today?
        </Body>
      </motion.div>

      {/* Statistics Overview -- four cards, all caller-scoped. We
          deliberately drop org-level counts ("Total Teachers", "Total
          Classrooms") -- those are admin-territory and don't help a
          teacher do their job. */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatisticsCard
          title="My Class"
          value={stats?.my_children ?? 0}
          icon={UserGroupIcon}
          color="primary"
        />
        <StatisticsCard
          title="My Observations"
          value={stats?.my_observations ?? 0}
          icon={ClipboardDocumentListIcon}
          color="secondary"
        />
        <StatisticsCard
          title="My Assessments"
          value={stats?.my_assessments ?? 0}
          icon={AcademicCapIcon}
          color="success"
        />
        <StatisticsCard
          title="This Week"
          value={stats?.my_observations_this_week ?? 0}
          icon={CalendarDaysIcon}
          color="warning"
        />
      </div>

      <div>
        <H2 className="mb-6">Quick Actions</H2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {quickActions.map((action, index) => (
            <motion.div
              key={action.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <Link to={action.href}>
                <Card className="p-6 hover:shadow-lg transition-all duration-200 hover:scale-105 cursor-pointer">
                  <div className="flex items-center mb-4">
                    <div className={`p-3 rounded-full ${
                      action.color === 'primary' ? 'bg-primary-100 text-primary-600' :
                      action.color === 'secondary' ? 'bg-secondary-100 text-secondary-600' :
                      action.color === 'success' ? 'bg-green-100 text-green-600' :
                      action.color === 'warning' ? 'bg-yellow-100 text-yellow-600' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      <action.icon className="w-6 h-6" />
                    </div>
                  </div>
                  <H2 className="text-lg mb-2">{action.title}</H2>
                  <Body className="text-sm text-gray-600">
                    {action.description}
                  </Body>
                </Card>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>

      {classrooms && classrooms.length > 0 && (
        <div>
          <H2 className="mb-6">My Classrooms</H2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {classrooms.map((classroom, index) => (
              <motion.div
                key={classroom.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <Link to={`/classrooms/${classroom.slug || classroom.id}`}>
                  <Card className="p-6 hover:shadow-lg transition-all duration-200 hover:scale-105 cursor-pointer">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <H2 className="text-lg mb-1">{classroom.name}</H2>
                        <Body className="text-sm text-gray-600">
                          {/* `grade_name` is the flat string the
                              /classrooms/with-stats endpoint actually
                              returns ("Grade 1", "Grade 2"). Falling back
                              to `classroom.grade` keeps callers that send
                              the expanded object happy; the em-dash is
                              the empty-state, not the literal word
                              "Grade" (which previously rendered as a bug). */}
                          {classroom.grade_name ||
                            (typeof classroom.grade === 'object'
                              ? classroom.grade?.name
                              : classroom.grade) ||
                            '—'}
                        </Body>
                      </div>
                      <div className="text-right">
                        <Body className="text-2xl font-bold text-primary-600">
                          {classroom.stats?.total_children ?? 0}
                        </Body>
                        <Body className="text-sm text-gray-500">children</Body>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <Body className="text-sm text-gray-600">
                        {classroom.stats?.total_observations ?? 0} observations
                      </Body>
                      <Button variant="ghost" size="sm">
                        View →
                      </Button>
                    </div>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      <div>
        <H2 className="mb-6">Recent Activity</H2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <H2 className="text-lg">Recent Observations</H2>
              <Link to="/observations">
                <Button variant="ghost" size="sm">
                  View all
                </Button>
              </Link>
            </div>
            <div className="space-y-3">
              {stats?.recent_observations && stats.recent_observations.length > 0 ? (
                stats.recent_observations.map((item) => (
                  <div key={item.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                    <div className="min-w-0 flex-1 mr-3">
                      <Body className="font-medium truncate">{item.child_name}</Body>
                      <Body className="text-sm text-gray-500">{item.category || 'General'}</Body>
                    </div>
                    <Body className="text-sm text-gray-500 whitespace-nowrap">
                      {formatRelative(item.observation_date)}
                    </Body>
                  </div>
                ))
              ) : (
                <Body className="text-gray-400 text-center py-4">No observations yet</Body>
              )}
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <H2 className="text-lg">Recent Assessments</H2>
              <Link to="/assessments">
                <Button variant="ghost" size="sm">
                  View all
                </Button>
              </Link>
            </div>
            <div className="space-y-3">
              {stats?.recent_assessments && stats.recent_assessments.length > 0 ? (
                stats.recent_assessments.map((item) => (
                  <div key={item.id} className="py-2 border-b border-gray-100 last:border-b-0">
                    <Body className="font-medium">{item.child_name}</Body>
                    <div className="flex items-center justify-between mt-1">
                      <Body className="text-sm text-gray-500">{item.learning_area_name}</Body>
                      <Body className="text-sm text-gray-500">
                        {formatRelative(item.assessment_date)}
                      </Body>
                    </div>
                  </div>
                ))
              ) : (
                <Body className="text-gray-400 text-center py-4">No assessments yet</Body>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TeacherDashboard;
